import { Currency } from "@bentolabs/kit/entities";
import { useMutation } from "@tanstack/react-query";
import invariant from "tiny-invariant";
import { getContract, parseUnits } from "viem";
import { waitForTransactionReceipt } from "viem/actions";
import { useChainId, useWalletClient } from "wagmi";

import { tellerABI } from "shared/config/abi";
import { bentoUSDConfig, tellerConfig } from "shared/config/const";
import { catchError, notifyPromise } from "shared/ui/toast";

type Params = {
  currency: Currency;
  amount: string;
  minimumAmount: string;
};

export const useDepositMutation = () => {
  const chainId = useChainId();
  const { data: wc } = useWalletClient();
  const bento = bentoUSDConfig[chainId];

  return useMutation({
    mutationFn: async (params: Params) => {
      const { currency, amount, minimumAmount } = params;
      invariant(wc, "useDepositMutation. wc is undefined");
      const contract = getContract({
        abi: tellerABI,
        address: tellerConfig[chainId],
        client: wc,
      });

      const parsedAmount = parseUnits(amount, currency.decimals);
      const parsedMinimumAmount = parseUnits(minimumAmount, bento.decimals);

      const preparedArgs = [
        currency.address,
        parsedAmount,
        parsedMinimumAmount,
      ] as const;

      const logParam = {
        contractName: "teller",
        contractAddress: tellerConfig[chainId],
        requestArgs: [
          preparedArgs[0],
          preparedArgs[1].toString(),
          preparedArgs[2].toString(),
        ],
        fromCurrency: currency,
        amount: `${amount} ${currency.symbol}`,
        minimumAmount: `${minimumAmount} ${bento.symbol}`,
      };
      console.log("DEPOSIT TX: ", logParam);
      console.log(JSON.stringify(logParam, null, 2));

      const hash = await contract.write.deposit(preparedArgs);
      const receiptPromise = waitForTransactionReceipt(wc, { hash });
      const receipt = await notifyPromise(receiptPromise, {
        pending: "Waiting for receipt",
        success: "Transaction success",
      });

      if (receipt.status === "reverted") {
        throw new Error("Transaction has been reverted");
      }

      return receipt;
    },
    onError: catchError,
  });
};
